import { useCallback } from 'react';
import { ApolloError } from '@apollo/client';
import { useSubmitStringGroupTranslationMutation } from 'src/generated/graphql';
import { DraftState } from '../types';

interface SubmitResult {
  success: boolean;
  version?: number;
  error?: string;
}

interface UseSubmitTranslationResult {
  submit: (notificationEmail: string) => Promise<SubmitResult>;
  loading: boolean;
}

/**
 * Submits a string group for translation via the SubmitStringGroupTranslation
 * GraphQL mutation. Converts the local DraftState into the mutation variables
 * (added/edited/deleted arrays + baseVersion + cxIds).
 *
 * CX ID resolution: if the user edited CX IDs in the draft (`groupEdits.cxIds`),
 * those are sent; otherwise the current server CX IDs are forwarded unchanged so
 * that submissions never accidentally clear the group's CX ID selection.
 */
export function useSubmitTranslation(
  namespaceId: string,
  stringGroupId: string,
  draftState: DraftState | null,
  serverCxIds: string[],
): UseSubmitTranslationResult {
  const [mutate, { loading }] = useSubmitStringGroupTranslationMutation();

  const submit = useCallback(
    async (notificationEmail: string): Promise<SubmitResult> => {
      if (!draftState) {
        return { success: false, error: 'No pending changes to submit.' };
      }

      // Resolve effective CX IDs: draft-edited values take precedence over
      // the current server state; unchanged submissions pass through the
      // server's current selection.
      const effectiveCxIds = draftState.groupEdits?.cxIds ?? serverCxIds;

      try {
        const { data } = await mutate({
          variables: {
            namespaceId,
            stringGroupId,
            baseVersion: draftState.baseVersion.toString(),
            added:
              draftState.added.length > 0
                ? draftState.added.map((s) => ({
                    stringId: s.stringId,
                    sourceLocale: s.sourceLocale,
                    sourceText: s.sourceText,
                    contextNote: s.contextNote,
                    inlineMarkupType: s.inlineMarkupType,
                    messageFormatType: s.messageFormatType,
                    variants: s.variants?.map((v) => ({
                      cxId: v.cxId,
                      sourceText: v.sourceText,
                      contextNote: v.contextNote,
                    })),
                  }))
                : undefined,
            edited:
              draftState.edited.length > 0
                ? draftState.edited.map((s) => ({
                    stringId: s.stringId,
                    sourceLocale: s.sourceLocale,
                    sourceText: s.sourceText,
                    contextNote: s.contextNote,
                    inlineMarkupType: s.inlineMarkupType,
                    messageFormatType: s.messageFormatType,
                    variants: s.variants?.map((v) => ({
                      cxId: v.cxId,
                      sourceText: v.sourceText,
                      contextNote: v.contextNote,
                    })),
                  }))
                : undefined,
            deleted:
              draftState.deleted.length > 0 ? draftState.deleted : undefined,
            cxIds: effectiveCxIds,
            notificationEmail: notificationEmail || undefined,
          },
        });

        const newVersion = data?.submitStringGroupTranslation.version;
        return { success: true, version: newVersion };
      } catch (e) {
        return { success: false, error: extractSubmitError(e) };
      }
    },
    [draftState, mutate, namespaceId, stringGroupId, serverCxIds],
  );

  return { submit, loading };
}

/** Extracts a user-friendly error message from a mutation failure. */
function extractSubmitError(e: unknown): string {
  const GENERIC_FALLBACK =
    'An error occurred while submitting. Please try again or contact support if the issue persists.';

  // Non-Error throwables can't originate from the Apollo mutation (it always
  // rejects with an Error/ApolloError); this guard is purely defensive.
  // istanbul ignore next -- unreachable via the mutation path
  if (!(e instanceof Error)) {
    return GENERIC_FALLBACK;
  }

  // ApolloError carries structured GraphQL errors with typed extensions
  if (e instanceof ApolloError && e.graphQLErrors.length > 0) {
    const gqlError = e.graphQLErrors[0];
    const errorType = gqlError.extensions?.['errorType'] as string | undefined;
    const msg = gqlError.message;

    // If the message is unhelpful (empty JSON, blank, etc.), provide a typed fallback
    if (!msg || !msg.trim() || /^\s*[{\[]/.test(msg.trim())) {
      switch (errorType) {
        case 'ConflictException':
          return 'A newer version was submitted concurrently. Please refresh and retry.';
        case 'ValidationException':
          return 'The submission was rejected due to a validation error. Please review your changes.';
        case 'AccessDeniedException':
          return 'You do not have permission to submit this string group for translation.';
        default:
          return GENERIC_FALLBACK;
      }
    }

    return msg;
  }

  // Fallback for non-Apollo errors (network errors, etc.)
  const raw = e.message;
  if (!raw || !raw.trim() || /^\s*[{\[]/.test(raw.trim())) {
    return GENERIC_FALLBACK;
  }
  return raw;
}
